import { useEffect, useState } from "react";
import { MdAdd, MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { AdminActionButton } from "../common/AdminActionButton";
import { AdminBanner } from "../common/AdminBanner";
import { AdminOrganizationPermissions } from "../../types/adminEntities";
import { hasOrganizationPermission, DisabledActionReason } from "../../utils/adminPermissions";
import { useAdminOrganizationContextCache } from "./AdminOrganizationContextCache";
import { AdminNavContext, AdminNavSelection } from "./AdminNavList";
import { OrganizationDetailSection } from "./detail/OrganizationDetailSection";
import { SignDetailSection } from "./detail/SignDetailSection";
import { SiteDetailSection } from "./detail/SiteDetailSection";
import { TranslationDetailSection } from "./detail/TranslationDetailSection";

type AdminRightPaneProps = {
  context: AdminNavContext;
  currentUserSuperAdmin: boolean;
  isDesktopNavHidden: boolean;
  isMobileViewport: boolean;
  onCloseDesktopNav: () => void;
  onEntityMutated: () => void;
  onOpenDesktopNav: () => void;
  onSelectionChange: (selection: AdminNavSelection | null) => void;
  selection: AdminNavSelection | null;
  t: (key: TranslationKey) => string;
};

const getPaneType = (context: AdminNavContext, selection: AdminNavSelection | null) => {
  if (selection?.type) {
    return selection.type;
  }
  if (context.level === "organizations") {
    return "organization";
  }
  if (context.level === "sites") {
    return "site";
  }
  if (context.level === "signs") {
    return "sign";
  }
  return "translation";
};

const getCreateActionLabelKey = (paneType: "organization" | "site" | "sign" | "translation"): TranslationKey => {
  if (paneType === "organization") {
    return "admin.detail.action.createOrganization";
  }
  if (paneType === "site") {
    return "admin.detail.action.createSite";
  }
  if (paneType === "sign") {
    return "admin.detail.action.createSign";
  }
  return "admin.detail.action.createTranslation";
};

export const AdminRightPane = ({
  context,
  currentUserSuperAdmin,
  isDesktopNavHidden,
  isMobileViewport,
  onCloseDesktopNav,
  onEntityMutated,
  onOpenDesktopNav,
  onSelectionChange,
  selection,
  t,
}: AdminRightPaneProps) => {
  const paneType = getPaneType(context, selection);
  const createActionLabel = t(getCreateActionLabelKey(paneType));
  const organizationId = selection?.organizationId ?? context.organizationId;
  const siteId = selection?.siteId ?? context.siteId;
  const signId = selection?.signId ?? context.signId;
  const translationId = selection?.translationId ?? null;
  const hasSelection = selection !== null;
  const isCreateSelection = Boolean(selection?.create);
  const { contextsByOrganizationId, ensureOrganizationContext } = useAdminOrganizationContextCache();
  const organizationContext = organizationId ? contextsByOrganizationId[organizationId] ?? null : null;
  const [organizationPermissions, setOrganizationPermissions] = useState<AdminOrganizationPermissions | null>(null);
  const [isExpiredBannerDismissed, setIsExpiredBannerDismissed] = useState(false);

  useEffect(() => {
    if (!organizationId || hasSelection) {
      setOrganizationPermissions(null);
      return;
    }
    const cachedContext = contextsByOrganizationId[organizationId];
    if (cachedContext) {
      setOrganizationPermissions(cachedContext.organizationPermissions);
      return;
    }
    let isCancelled = false;
    void ensureOrganizationContext(organizationId).then((resolvedContext) => {
      if (!isCancelled) {
        setOrganizationPermissions(resolvedContext?.organizationPermissions ?? null);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [organizationId, contextsByOrganizationId, ensureOrganizationContext, hasSelection]);

  const isOrganizationExpired = Boolean(
    organizationContext
    && (organizationContext.activeSubscription.expiresAt === null || new Date(organizationContext.activeSubscription.expiresAt).getTime() <= Date.now())
  );
  const shouldShowExpiredBanner = isOrganizationExpired
    && !isExpiredBannerDismissed
    && !hasOrganizationPermission(organizationContext?.organizationPermissions ?? null, "ORGANIZATION_ACTIVE_SUBSCRIPTION_DIRECT_UPDATE");

  useEffect(() => {
    setIsExpiredBannerDismissed(false);
  }, [organizationId, isOrganizationExpired]);

  const canCreateInEmptyPane = paneType === "organization"
    || (paneType === "site" && hasOrganizationPermission(organizationPermissions, "SITE_CREATE") && !!organizationContext && organizationContext.organizationUsage.activeSiteCount < organizationContext.activeSubscription.maxActiveSites)
    || (paneType === "sign" && hasOrganizationPermission(organizationPermissions, "SIGN_CREATE") && !!organizationContext && organizationContext.organizationUsage.activeSignCount < organizationContext.activeSubscription.maxActiveSigns)
    || (paneType === "translation" && hasOrganizationPermission(organizationPermissions, "TRANSLATION_CREATE") && !!organizationContext && organizationContext.organizationUsage.activeTranslationCount < organizationContext.activeSubscription.maxActiveTranslations);
  const createDisabledReason: DisabledActionReason = paneType === "organization"
    ? null
    : paneType === "site"
      ? organizationContext && !hasOrganizationPermission(organizationPermissions, "SITE_CREATE")
        ? "permission"
        : organizationContext && organizationContext.organizationUsage.activeSiteCount >= organizationContext.activeSubscription.maxActiveSites
          ? "subscription-limit"
          : null
      : paneType === "sign"
        ? organizationContext && !hasOrganizationPermission(organizationPermissions, "SIGN_CREATE")
          ? "permission"
          : organizationContext && organizationContext.organizationUsage.activeSignCount >= organizationContext.activeSubscription.maxActiveSigns
            ? "subscription-limit"
            : null
        : organizationContext && !hasOrganizationPermission(organizationPermissions, "TRANSLATION_CREATE")
          ? "permission"
          : organizationContext && organizationContext.organizationUsage.activeTranslationCount >= organizationContext.activeSubscription.maxActiveTranslations
            ? "subscription-limit"
            : null;
  const paneTopActions = (
    <div className="admin-right-pane-top-actions">
      {!isMobileViewport ? (
        <button
          aria-label={isDesktopNavHidden ? t("admin.detail.action.showNavigator") : t("admin.detail.action.hideNavigator")}
          className="admin-detail-action"
          onClick={isDesktopNavHidden ? onOpenDesktopNav : onCloseDesktopNav}
          title={isDesktopNavHidden ? t("admin.detail.action.showNavigator") : t("admin.detail.action.hideNavigator")}
          type="button"
        >
          {isDesktopNavHidden ? <MdFullscreenExit /> : <MdFullscreen />}
        </button>
      ) : null}
      <AdminActionButton
        aria-label={createActionLabel}
        className="admin-detail-action"
        disabledReason={createDisabledReason}
        disabled={!canCreateInEmptyPane}
        onClick={() => {
          if (!canCreateInEmptyPane) {
            return;
          }
          if (paneType === "organization") {
            onSelectionChange({ create: true, type: "organization" });
          } else if (paneType === "site" && organizationId) {
            onSelectionChange({ organizationId, create: true, type: "site" });
          } else if (paneType === "sign" && organizationId && siteId) {
            onSelectionChange({ organizationId, create: true, siteId, type: "sign" });
          } else if (paneType === "translation" && organizationId && siteId && signId) {
            onSelectionChange({ organizationId, create: true, signId, siteId, type: "translation" });
          }
        }}
        t={t}
        title={createActionLabel}
      >
        <MdAdd />
      </AdminActionButton>
    </div>
  );
  return (
    <section className="admin-shell-right-pane">
      {shouldShowExpiredBanner ? (
        <AdminBanner
          color="red"
          onClose={() => setIsExpiredBannerDismissed(true)}
        >
          {t("admin.subscription.expiredBanner")}
        </AdminBanner>
      ) : null}
      {!hasSelection ? (
        <div className="admin-right-pane-empty-state">
          <div className="admin-right-pane-top-actions admin-right-pane-top-actions-floating">
            {paneTopActions.props.children}
          </div>
          <div className="admin-right-pane-empty-copy">
            <p className="admin-right-pane-empty-text">{t("admin.detail.helper")}</p>
          </div>
        </div>
      ) : null}
      {paneType === "organization" && hasSelection ? (
        <OrganizationDetailSection
          key={`organization:${selection.create ? "create" : selection.organizationId ?? "none"}:${selection.organizationTab ?? "details"}`}
          currentUserSuperAdmin={currentUserSuperAdmin}
          organizationId={selection.type === "organization" && !isCreateSelection ? selection.organizationId ?? null : null}
          organizationTab={selection.type === "organization" && !isCreateSelection ? selection.organizationTab ?? "details" : "details"}
          isActive
          isDesktopNavHidden={isDesktopNavHidden}
          isMobileViewport={isMobileViewport}
          onCloseDesktopNav={onCloseDesktopNav}
          onEntityMutated={onEntityMutated}
          onOpenDesktopNav={onOpenDesktopNav}
          onSelectionChange={onSelectionChange}
          t={t}
        />
      ) : null}
      {paneType === "site" && hasSelection ? (
        <SiteDetailSection
          key={`site:${isCreateSelection ? "create" : selection.siteId ?? "none"}:${organizationId ?? "none"}`}
          organizationId={organizationId}
          isActive
          isDesktopNavHidden={isDesktopNavHidden}
          isMobileViewport={isMobileViewport}
          onCloseDesktopNav={onCloseDesktopNav}
          onEntityMutated={onEntityMutated}
          onOpenDesktopNav={onOpenDesktopNav}
          onSelectionChange={onSelectionChange}
          siteId={selection.type === "site" && !isCreateSelection ? selection.siteId ?? null : null}
          t={t}
        />
      ) : null}
      {paneType === "sign" && hasSelection ? (
        <SignDetailSection
          key={`sign:${isCreateSelection ? "create" : selection.signId ?? "none"}:${selection.signTab ?? "details"}:${organizationId ?? "none"}:${siteId ?? "none"}`}
          currentUserSuperAdmin={currentUserSuperAdmin}
          organizationId={organizationId}
          isActive
          isDesktopNavHidden={isDesktopNavHidden}
          isMobileViewport={isMobileViewport}
          onCloseDesktopNav={onCloseDesktopNav}
          onEntityMutated={onEntityMutated}
          onOpenDesktopNav={onOpenDesktopNav}
          onSelectionChange={onSelectionChange}
          signTab={selection.type === "sign" && !isCreateSelection ? selection.signTab ?? "details" : "details"}
          signId={selection.type === "sign" && !isCreateSelection ? selection.signId ?? null : null}
          siteId={siteId}
          t={t}
        />
      ) : null}
      {paneType === "translation" && hasSelection ? (
        <TranslationDetailSection
          key={`translation:${isCreateSelection ? "create" : translationId ?? "none"}:${organizationId ?? "none"}:${siteId ?? "none"}:${signId ?? "none"}`}
          organizationId={organizationId}
          isActive
          isDesktopNavHidden={isDesktopNavHidden}
          isMobileViewport={isMobileViewport}
          onCloseDesktopNav={onCloseDesktopNav}
          onEntityMutated={onEntityMutated}
          onOpenDesktopNav={onOpenDesktopNav}
          onSelectionChange={onSelectionChange}
          signId={signId}
          siteId={siteId}
          t={t}
          translationId={selection.type === "translation" && !isCreateSelection ? translationId : null}
        />
      ) : null}
    </section>
  );
};
